/**
 * The first `EditorServices` adapter (1.1 deliverable 21): every method of 1.0b's contract over
 * the real routes, through the typed client. A method whose route's owner has not merged rejects
 * with what the server answers (404 `not_found` from the core's `/api/*` fallback); nothing here
 * fakes a success. A 409 rejects as contracts' `ConflictError`, whose details parse as
 * `VersionConflictDetailsV1` for a stale base.
 *
 * Secret states (1.Y deliverable 9): with `ledger` on, `subscribeSecretState` is 1.M-2's
 * `subscribeSecretStates` (`secrets.forDoc`, again on each `ledger.changed` naming the page); with
 * it off, or no flags given, every page answers an empty list once and no `secrets.forDoc` is sent.
 */
import {
  assetsFetch,
  assetsUpload,
  type DocTitleV1,
  type DocTypeSummaryV1,
  docsCreate,
  docsGet,
  docsMeta,
  docsMoveBlocks,
  docsPatchMeta,
  docsPutBody,
  docsTitles,
  type EditorServices,
  type Flags,
  rulesGet,
  type SecretStateV1,
  searchPicker,
  sheetsCharacterGet,
  sheetsGet,
  sheetsList,
  typesList,
} from '@codex/contracts';
import { type ApiClient, api as defaultApi } from '@codex/contracts/client';
import {
  type SubscribeSecretStates,
  subscribeSecretStates,
} from '../ledger/secret-state-source.js';
import { type AppEvents, appEvents } from './events.js';

/** `docs.titles` answers at most this many ids per request. */
export const TITLES_PER_REQUEST = 500;

export interface EditorServicesDeps {
  /** The typed client; contracts' same-origin `api` by default. Specs pass `createApiClient`. */
  readonly api?: ApiClient;
  /** The live-update client; the tab's `appEvents` by default. */
  readonly events?: Pick<AppEvents, 'subscribeDocChanged'>;
  /**
   * Goes to an address inside the app (`/doc/<id>#<blockId>`): the router's navigation, or a
   * plain page load by default. Never a new browser tab or a download (A2.1 section 7, fix 10).
   */
  readonly navigate?: (href: string) => void;
  /** The signed-in GM's flags (`flagsFromMe(me)`); absent, every flag reads off. */
  readonly flags?: Pick<Flags, 'ledger'> | null;
  /** The secret-state source while `ledger` is on; 1.M-2's app instance by default. Specs pass a fake. */
  readonly secretStates?: SubscribeSecretStates;
}

/** No secret has a state: one empty answer, after the subscription returns. */
const noSecretStates: SubscribeSecretStates = (_docId, listener) => {
  let active = true;
  queueMicrotask(() => {
    if (active) listener([] as readonly SecretStateV1[]);
  });
  return () => {
    active = false;
  };
};

/** The address of a document, with the block as the hash. */
export function docHref(docId: string, blockId?: string): string {
  const hash = blockId === undefined ? '' : `#${encodeURIComponent(blockId)}`;
  return `/doc/${encodeURIComponent(docId)}${hash}`;
}

/** Distinct ids in the order first asked. */
function distinct(ids: readonly string[]): string[] {
  return [...new Set(ids)];
}

function summaryOf(type: DocTypeSummaryV1): DocTypeSummaryV1 {
  return {
    id: type.id,
    key: type.key,
    name: type.name,
    namePlural: type.namePlural,
    icon: type.icon,
    color: type.color,
    library: type.library,
    capabilities: [...type.capabilities],
    archived: type.archived,
  };
}

export function createEditorServices(deps: EditorServicesDeps = {}): EditorServices {
  const api = deps.api ?? defaultApi;
  const events = deps.events ?? appEvents;
  const navigate = deps.navigate ?? ((href: string) => window.location.assign(href));
  const secretStateSource: SubscribeSecretStates =
    deps.flags?.ledger === true ? (deps.secretStates ?? subscribeSecretStates) : noSecretStates;

  return {
    searchDocs: (query, opts) =>
      api(searchPicker, { query }, opts?.signal ? { signal: opts.signal } : {}),

    async createStub({ typeKey, title }) {
      const created = await api(docsCreate, { body: { typeKey, title, isStub: true } });
      return api(docsMeta, { params: { id: created.id } });
    },

    getDoc: (docId) => api(docsGet, { params: { id: docId } }),

    getDocMeta: (docId) => api(docsMeta, { params: { id: docId } }),

    async resolveTitles(ids) {
      const wanted = distinct(ids);
      const answers: DocTitleV1[] = [];
      for (let start = 0; start < wanted.length; start += TITLES_PER_REQUEST) {
        const batch = wanted.slice(start, start + TITLES_PER_REQUEST);
        const { titles } = await api(docsTitles, { body: { ids: batch } });
        answers.push(...titles);
      }
      return answers;
    },

    async listTypes() {
      const { items } = await api(typesList);
      return items.map(summaryOf);
    },

    uploadAsset: (file, { docId, blockId, signal }) =>
      api(assetsUpload, { body: { docId, blockId }, file }, signal ? { signal } : {}),

    // The signal is the call's abort option, never a body key: `AssetFetchRequestV1` is strict
    // and answers 400 to an unrecognized `signal` (the paste follow-ups always pass one).
    rehostAsset: ({ url, docId, blockId, notionBlockId, signal }) =>
      api(
        assetsFetch,
        {
          body: { url, docId, blockId, ...(notionBlockId === undefined ? {} : { notionBlockId }) },
        },
        signal ? { signal } : {},
      ),

    subscribeSecretState: secretStateSource,

    subscribeDocChanged: (docId, listener) => events.subscribeDocChanged(docId, listener),

    openDoc({ docId, blockId }, _mode) {
      // `peek`, `center` and `split` open the full page until 1.I-2's navigation exists.
      navigate(docHref(docId, blockId));
    },

    save: (docId, request, opts) =>
      api(
        docsPutBody,
        { params: { id: docId }, body: request },
        opts?.signal ? { signal: opts.signal } : {},
      ),

    updateMeta: (docId, request) => api(docsPatchMeta, { params: { id: docId }, body: request }),

    moveBlocks: (docId, request) => api(docsMoveBlocks, { params: { id: docId }, body: request }),

    getSheet: (sheetId) => api(sheetsGet, { params: { sheetId } }),

    getRuleCard: (docId) => api(rulesGet, { params: { id: docId } }),

    async listSheets(docId, opts) {
      const call = opts?.signal ? { signal: opts.signal } : {};
      return (await api(sheetsList, { params: { id: docId } }, call)).items;
    },

    getCharacterSheet: (sheetId) => api(sheetsCharacterGet, { params: { sheetId } }),
  };
}
